import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {
  useCreateGoogleUserMailboxMutation,
  useCreateMicrosoftUserMailboxMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {buildUrl, userMailboxOAuthFinish} from '@dms/routes';
import {handleApiError} from '@dms/shared';

const ConnectOAuthMailboxFormSchema = z.object({
  name: z.string().min(1, i18n.t('general.validations.fieldIsRequired')).max(255),
});

type ConnectOAuthMailboxFormValues = z.infer<typeof ConnectOAuthMailboxFormSchema>;

interface ConnectOAuthMailboxFormProps {
  provider: 'google' | 'microsoft';
}

export function ConnectOAuthMailboxForm(props: ConnectOAuthMailboxFormProps) {
  const [createGoogleUserMailbox] = useCreateGoogleUserMailboxMutation();
  const [createMicrosoftUserMailbox] = useCreateMicrosoftUserMailboxMutation();

  const handleSubmit: FormSubmitHandler<ConnectOAuthMailboxFormValues> = async (values) => {
    const redirectUri = buildUrl(userMailboxOAuthFinish, undefined, false);

    const startOAuth =
      props.provider === 'google'
        ? createGoogleUserMailbox({
            createUserGoogleMailboxRequestBody: {name: values.name, redirectUri},
          })
        : createMicrosoftUserMailbox({
            createUserMicrosoftMailboxRequestBody: {name: values.name, redirectUri},
          });

    await startOAuth
      .unwrap()
      .then(({oauthUrl}) => {
        window.location.href = oauthUrl;
      })
      .catch(handleApiError);
  };

  return (
    <Form<ConnectOAuthMailboxFormValues>
      onSubmit={handleSubmit}
      experimentalZodSchema={ConnectOAuthMailboxFormSchema}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            name="name"
            type="text"
            label={i18n.t('entity.mailbox.labels.name')}
            tooltip={i18n.t('entity.mailbox.labels.nameTooltip')}
            control={control}
            isRequired
          />
          <Separator />
          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.cancel')}
              variant="secondary"
              onClick={closeCurrentDialog}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('page.myProfile.receivingMailbox.continueToProvider')}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
